import React, { useState, useEffect, useMemo } from 'react';
import { AdminHeader } from './AdminLayout';
import { 
    getAngebote, 
    getLehrpersonen, 
    getUnterrichtsorte, 
    getInstrumente,
    getGemeinden,
    getDashboardStats
} from '@/lib/api';
import { Input } from '@/components/ui/input';
import { 
    Search,
    X,
    Clock,
    Building2,
    MapPin,
    Music,
    Calendar,
    Users,
    Layers
} from 'lucide-react';
import { cn } from '@/lib/utils';

const WOCHENTAGE_ORDER = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WOCHENTAGE_SHORT = { 'Montag': 'Mo', 'Dienstag': 'Di', 'Mittwoch': 'Mi', 'Donnerstag': 'Do', 'Freitag': 'Fr', 'Samstag': 'Sa' };

export function AngeboteUebersicht() {
    const [angebote, setAngebote] = useState([]);
    const [lehrpersonen, setLehrpersonen] = useState([]);
    const [unterrichtsorte, setUnterrichtsorte] = useState([]);
    const [instrumente, setInstrumente] = useState([]);
    const [gemeinden, setGemeinden] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState({ type: null, id: null, name: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [angeboteRes, lehrRes, orteRes, instrRes, gemRes, statsRes] = await Promise.all([
                getAngebote(),
                getLehrpersonen(),
                getUnterrichtsorte(),
                getInstrumente(),
                getGemeinden(),
                getDashboardStats()
            ]);
            setAngebote(angeboteRes.data);
            setLehrpersonen(lehrRes.data.sort((a, b) => a.nachname.localeCompare(b.nachname)));
            setUnterrichtsorte(orteRes.data);
            setInstrumente(instrRes.data.sort((a, b) => a.name.localeCompare(b.name)));
            setGemeinden(gemRes.data.sort((a, b) => a.name.localeCompare(b.name)));
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Calculate offer counts per entity
    const angeboteByLehrer = useMemo(() => {
        const counts = {};
        angebote.forEach(a => {
            counts[a.lehrperson_id] = (counts[a.lehrperson_id] || 0) + 1;
        });
        return counts;
    }, [angebote]);

    const angeboteByInstrument = useMemo(() => {
        const counts = {};
        angebote.forEach(a => {
            // Unterstütze sowohl instrument_ids Array als auch einzelne instrument_id
            const ids = a.instrument_ids?.length > 0 ? a.instrument_ids : (a.instrument_id ? [a.instrument_id] : []);
            ids.forEach(id => {
                counts[id] = (counts[id] || 0) + 1;
            });
        });
        return counts;
    }, [angebote]);

    const angeboteByGemeinde = useMemo(() => {
        const counts = {};
        angebote.forEach(a => {
            counts[a.gemeinde_id] = (counts[a.gemeinde_id] || 0) + 1;
        });
        return counts;
    }, [angebote]);

    // Filter angebote based on active filter
    const filteredAngebote = useMemo(() => {
        if (!activeFilter.type) return angebote;
        
        switch (activeFilter.type) {
            case 'lehrperson':
                return angebote.filter(a => a.lehrperson_id === activeFilter.id);
            case 'unterrichtsort':
                return angebote.filter(a => a.unterrichtsort_id === activeFilter.id);
            case 'gemeinde':
                return angebote.filter(a => a.gemeinde_id === activeFilter.id);
            case 'instrument':
                return angebote.filter(a => {
                    if (Array.isArray(a.instrument_ids) && a.instrument_ids.includes(activeFilter.id)) {
                        return true;
                    }
                    if (a.instrument_id === activeFilter.id) {
                        return true;
                    }
                    return false;
                });
            case 'wochentag':
                return angebote.filter(a => a.wochentag === activeFilter.id);
            default:
                return angebote;
        }
    }, [angebote, activeFilter]);

    // Group filtered results
    const groupedResults = useMemo(() => {
        const byDay = {};
        filteredAngebote.forEach(a => {
            if (!byDay[a.wochentag]) byDay[a.wochentag] = [];
            byDay[a.wochentag].push(a);
        });
        // Sort each day by time
        Object.keys(byDay).forEach(day => {
            byDay[day].sort((a, b) => a.zeit_von.localeCompare(b.zeit_von));
        });
        return byDay;
    }, [filteredAngebote]);

    // Search suggestions - show ALL matching entities
    const searchResults = useMemo(() => {
        if (!searchTerm || searchTerm.length < 2) return { lehrpersonen: [], orte: [], instrumente: [], gemeinden: [] };
        
        const term = searchTerm.toLowerCase();
        
        return {
            lehrpersonen: lehrpersonen.filter(l => 
                `${l.vorname} ${l.nachname}`.toLowerCase().includes(term) ||
                l.nachname.toLowerCase().includes(term)
            ).slice(0, 8),
            gemeinden: gemeinden.filter(g =>
                g.name.toLowerCase().includes(term)
            ).slice(0, 6),
            orte: unterrichtsorte.filter(o => 
                o.name.toLowerCase().includes(term)
            ).slice(0, 6),
            instrumente: instrumente.filter(i => 
                i.name.toLowerCase().includes(term)
            ).slice(0, 6)
        };
    }, [searchTerm, lehrpersonen, unterrichtsorte, instrumente, gemeinden]);

    const hasSearchResults = searchResults.lehrpersonen.length > 0 || 
        searchResults.orte.length > 0 || 
        searchResults.instrumente.length > 0 ||
        searchResults.gemeinden.length > 0;

    const setFilter = (type, id, name) => {
        setActiveFilter({ type, id, name });
        setSearchTerm('');
    };

    const clearFilter = () => {
        setActiveFilter({ type: null, id: null, name: null });
    };

    // Chip styles by category
    const chipStyles = {
        lehrperson: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200',
        gemeinde: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200',
        unterrichtsort: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200',
        instrument: 'bg-violet-50 text-violet-700 hover:bg-violet-100 border-violet-200',
        wochentag: 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200',
    };

    const activeChipStyles = {
        lehrperson: 'bg-blue-500 text-white',
        gemeinde: 'bg-emerald-500 text-white',
        unterrichtsort: 'bg-amber-500 text-white',
        instrument: 'bg-violet-500 text-white',
        wochentag: 'bg-slate-600 text-white',
    };

    return (
        <>
            <AdminHeader title="Übersicht" />

            <div className="admin-content max-w-6xl">
                {/* Stats Row - compact */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-8">
                    {[
                        { key: 'angebote_count', label: 'Angebote', icon: Calendar, color: 'text-primary', bg: 'bg-blue-50' },
                        { key: 'lehrpersonen_count', label: 'Lehrer', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
                        { key: 'gemeinden_count', label: 'Gemeinden', icon: Building2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                        { key: 'unterrichtsorte_count', label: 'Orte', icon: MapPin, color: 'text-amber-600', bg: 'bg-amber-50' },
                        { key: 'instrumente_count', label: 'Instrumente', icon: Music, color: 'text-violet-600', bg: 'bg-violet-50' },
                        { key: 'instrumentengruppen_count', label: 'Gruppen', icon: Layers, color: 'text-teal-600', bg: 'bg-teal-50' },
                    ].map((item) => {
                        const Icon = item.icon;
                        const value = stats ? stats[item.key] : 0;
                        return (
                            <div key={item.key} className="flex items-center gap-3 p-3 bg-card rounded-xl border">
                                <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", item.bg)}>
                                    <Icon className={cn("w-5 h-5", item.color)} />
                                </div>
                                <div>
                                    <p className={cn("text-xl font-bold", item.color)}>{loading ? '-' : value}</p>
                                    <p className="text-xs text-muted-foreground">{item.label}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Search Bar - Unified Style */}
                <div className="flex flex-wrap items-center gap-4 mb-8">
                    <div className="search-wrapper flex-1 max-w-xl relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors" />
                        <Input
                            placeholder="Suchen nach Lehrer, Gemeinde, Instrument..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-muted"
                            >
                                <X className="h-5 w-5 text-muted-foreground" />
                            </button>
                        )}
                        
                        {/* Search Dropdown */}
                        {hasSearchResults && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 rounded-2xl shadow-xl z-50 overflow-hidden p-3">
                                <div className="flex flex-wrap gap-2">
                                    {searchResults.lehrpersonen.map(l => {
                                        const count = angeboteByLehrer[l.id] || 0;
                                        return (
                                            <button
                                                key={`l-${l.id}`}
                                                onClick={() => setFilter('lehrperson', l.id, `${l.vorname} ${l.nachname}`)}
                                                className={cn(
                                                    "px-4 py-2 rounded-full text-sm font-medium border transition-colors",
                                                    chipStyles.lehrperson,
                                                    count === 0 && "opacity-60"
                                                )}
                                            >
                                                {l.vorname} {l.nachname}
                                                <span className={cn(
                                                    "ml-1.5 text-xs",
                                                    count === 0 ? "text-blue-400" : "text-blue-600"
                                                )}>
                                                    ({count})
                                                </span>
                                            </button>
                                        );
                                    })}
                                    {searchResults.gemeinden.map(g => {
                                        const count = angeboteByGemeinde[g.id] || 0;
                                        return (
                                            <button
                                                key={`g-${g.id}`}
                                                onClick={() => setFilter('gemeinde', g.id, g.name)}
                                                className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors", chipStyles.gemeinde)}
                                            >
                                                {g.name}
                                                <span className="ml-1.5 text-xs text-emerald-600">({count})</span>
                                            </button>
                                        );
                                    })}
                                    {searchResults.orte.map(o => (
                                        <button
                                            key={`o-${o.id}`}
                                            onClick={() => setFilter('unterrichtsort', o.id, o.name)}
                                            className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors", chipStyles.unterrichtsort)}
                                        >
                                            {o.name}
                                        </button>
                                    ))}
                                    {searchResults.instrumente.map(i => {
                                        const count = angeboteByInstrument[i.id] || 0;
                                        return (
                                            <button
                                                key={`i-${i.id}`}
                                                onClick={() => setFilter('instrument', i.id, i.name)}
                                                className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors", chipStyles.instrument)}
                                            >
                                                {i.name}
                                                <span className="ml-1.5 text-xs text-violet-600">({count})</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="text-base text-muted-foreground">
                        {angebote.length} Angebote
                    </div>
                </div>

                {/* Active Filter */}
                {activeFilter.type && (
                    <div className="mb-8">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={clearFilter}
                                className={cn(
                                    "inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-base font-semibold transition-colors",
                                    activeChipStyles[activeFilter.type]
                                )}
                            >
                                {activeFilter.name}
                                <X className="h-4 w-4" />
                            </button>
                            <span className="text-lg text-muted-foreground">
                                {filteredAngebote.length} Angebote
                            </span>
                        </div>
                    </div>
                )}

                {/* Results when filtered - Clean Table View */}
                {activeFilter.type && (
                    <div className="space-y-6">
                        {WOCHENTAGE_ORDER.map(tag => {
                            const items = groupedResults[tag];
                            if (!items || items.length === 0) return null;
                            
                            return (
                                <div key={tag}>
                                    <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-3">
                                        <span className={cn(
                                            "px-3 py-1 rounded-lg text-sm font-semibold",
                                            "bg-slate-100 text-slate-600"
                                        )}>
                                            {tag}
                                        </span>
                                        <span className="text-muted-foreground font-normal text-base">
                                            {items.length} {items.length === 1 ? 'Angebot' : 'Angebote'}
                                        </span>
                                    </h3>
                                    <div className="data-card">
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th className="w-32">Zeit</th>
                                                    <th>Instrument</th>
                                                    {activeFilter.type !== 'lehrperson' && <th>Lehrperson</th>}
                                                    {activeFilter.type !== 'gemeinde' && activeFilter.type !== 'unterrichtsort' && <th>Ort</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map(a => (
                                                    <tr key={a.id}>
                                                        <td>
                                                            <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                                                                <Clock className="h-4 w-4 text-slate-400" />
                                                                {a.zeit_von} - {a.zeit_bis}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button 
                                                                onClick={() => {
                                                                    const firstId = a.instrument_ids?.[0] || a.instrument_id;
                                                                    setFilter('instrument', firstId, a.instrument_name);
                                                                }}
                                                                className={cn(
                                                                    "inline-block px-3 py-1 rounded-full text-sm font-medium border cursor-pointer hover:ring-2 hover:ring-offset-1",
                                                                    chipStyles.instrument
                                                                )}
                                                            >
                                                                {a.instrument_name}
                                                            </button>
                                                        </td>
                                                        {activeFilter.type !== 'lehrperson' && (
                                                            <td>
                                                                <button 
                                                                    onClick={() => setFilter('lehrperson', a.lehrperson_id, a.lehrperson_name)}
                                                                    className={cn(
                                                                        "inline-block px-3 py-1 rounded-full text-sm font-medium border cursor-pointer hover:ring-2 hover:ring-offset-1",
                                                                        chipStyles.lehrperson
                                                                    )}
                                                                >
                                                                    {a.lehrperson_name}
                                                                </button>
                                                            </td>
                                                        )}
                                                        {activeFilter.type !== 'gemeinde' && activeFilter.type !== 'unterrichtsort' && (
                                                            <td>
                                                                <div className="flex flex-wrap gap-2">
                                                                    <button 
                                                                        onClick={() => setFilter('gemeinde', a.gemeinde_id, a.gemeinde_name)}
                                                                        className={cn(
                                                                            "inline-block px-3 py-1 rounded-full text-sm font-medium border cursor-pointer hover:ring-2 hover:ring-offset-1",
                                                                            chipStyles.gemeinde
                                                                        )}
                                                                    >
                                                                        {a.gemeinde_name}
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => setFilter('unterrichtsort', a.unterrichtsort_id, a.unterrichtsort_name)}
                                                                        className={cn(
                                                                            "inline-block px-3 py-1 rounded-full text-sm font-medium border cursor-pointer hover:ring-2 hover:ring-offset-1",
                                                                            chipStyles.unterrichtsort
                                                                        )}
                                                                    >
                                                                        {a.unterrichtsort_name}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Default view - all filters as chips */}
                {!activeFilter.type && !loading && (
                    <div className="space-y-10">
                        {/* Wochentage */}
                        <div>
                            <h2 className="text-lg font-bold text-foreground mb-4">Wochentage</h2>
                            <div className="flex flex-wrap gap-2">
                                {WOCHENTAGE_ORDER.map(tag => {
                                    const count = angebote.filter(a => a.wochentag === tag).length;
                                    if (count === 0) return null;
                                    return (
                                        <button
                                            key={tag}
                                            onClick={() => setFilter('wochentag', tag, tag)}
                                            className={cn(
                                                "px-4 py-2 rounded-full text-base font-medium border transition-colors",
                                                chipStyles.wochentag
                                            )}
                                        >
                                            {tag} <span className="opacity-60">({count})</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Gemeinden */}
                        <div>
                            <h2 className="text-lg font-bold text-foreground mb-4">Gemeinden</h2>
                            <div className="flex flex-wrap gap-2">
                                {gemeinden.map(g => {
                                    const count = angeboteByGemeinde[g.id] || 0;
                                    return (
                                        <button
                                            key={g.id}
                                            onClick={() => setFilter('gemeinde', g.id, g.name)}
                                            className={cn(
                                                "px-4 py-2 rounded-full text-base font-medium border transition-colors",
                                                chipStyles.gemeinde,
                                                count === 0 && "opacity-50"
                                            )}
                                        >
                                            {g.name} <span className="opacity-60">({count})</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Unterrichtsorte */}
                        <div>
                            <h2 className="text-lg font-bold text-foreground mb-4">Unterrichtsorte</h2>
                            <div className="flex flex-wrap gap-2">
                                {unterrichtsorte.map(o => {
                                    const count = angebote.filter(a => a.unterrichtsort_id === o.id).length;
                                    return (
                                        <button
                                            key={o.id}
                                            onClick={() => setFilter('unterrichtsort', o.id, `${o.name} (${o.gemeinde_name})`)}
                                            className={cn(
                                                "px-4 py-2 rounded-full text-base font-medium border transition-colors",
                                                chipStyles.unterrichtsort,
                                                count === 0 && "opacity-50"
                                            )}
                                        >
                                            {o.name} <span className="text-xs opacity-70">({o.gemeinde_name})</span> <span className="opacity-60">({count})</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Lehrpersonen - ALLE anzeigen */}
                        <div>
                            <h2 className="text-lg font-bold text-foreground mb-4">Lehrpersonen</h2>
                            <div className="flex flex-wrap gap-2">
                                {lehrpersonen.map(l => {
                                    const count = angeboteByLehrer[l.id] || 0;
                                    return (
                                        <button
                                            key={l.id}
                                            onClick={() => setFilter('lehrperson', l.id, `${l.vorname} ${l.nachname}`)}
                                            className={cn(
                                                "px-4 py-2 rounded-full text-base font-medium border transition-colors",
                                                chipStyles.lehrperson,
                                                count === 0 && "opacity-50"
                                            )}
                                        >
                                            {l.vorname} {l.nachname} <span className={cn("opacity-60", count === 0 && "text-red-500")}>({count})</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Instrumente */}
                        <div>
                            <h2 className="text-lg font-bold text-foreground mb-4">Instrumente</h2>
                            <div className="flex flex-wrap gap-2">
                                {instrumente
                                    .filter(i => angebote.some(a => {
                                        const ids = a.instrument_ids?.length > 0 ? a.instrument_ids : (a.instrument_id ? [a.instrument_id] : []);
                                        return ids.includes(i.id);
                                    }))
                                    .map(i => {
                                        const count = angebote.filter(a => {
                                            const ids = a.instrument_ids?.length > 0 ? a.instrument_ids : (a.instrument_id ? [a.instrument_id] : []);
                                            return ids.includes(i.id);
                                        }).length;
                                        return (
                                            <button
                                                key={i.id}
                                                onClick={() => setFilter('instrument', i.id, i.name)}
                                                className={cn(
                                                    "px-4 py-2 rounded-full text-base font-medium border transition-colors",
                                                    chipStyles.instrument
                                                )}
                                            >
                                                {i.name} <span className="opacity-60">({count})</span>
                                            </button>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading state */}
                {loading && (
                    <div className="flex flex-wrap gap-3">
                        {[...Array(20)].map((_, i) => (
                            <div key={i} className="h-10 w-32 bg-muted rounded-full animate-pulse" />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
